import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Headcount drives regulatory HCI/officer caps; shareholders are uncapped but still require headcount > 0
 * before adding (same as UI). Caps are evaluated before setFormData so the modal never closes on a no-op add.
 */

export type DisqualifiedIndividualCategory = 'hci' | 'officer' | 'shareholder';

export type DisqualifiedIndividualRow = {
  id: string;
  name: string;
  email?: string;
  title: string;
  category: DisqualifiedIndividualCategory;
};

function newIndividualId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `ind-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

type FormSlice = {
  headcount?: string | number;
  individuals?: DisqualifiedIndividualRow[];
};

export function hciLimitFromHeadcount(n: number): number {
  if (!n) return 0;
  return Math.min(250, Math.max(1, Math.ceil(n * 0.01)));
}

export function officerLimitFromHeadcount(n: number): number {
  if (!n) return 0;
  return Math.min(50, Math.max(3, Math.ceil(n * 0.1)));
}

export function partitionDisqualifiedIndividuals(
  individuals: DisqualifiedIndividualRow[]
): Record<DisqualifiedIndividualCategory, DisqualifiedIndividualRow[]> {
  const hci: DisqualifiedIndividualRow[] = [];
  const officer: DisqualifiedIndividualRow[] = [];
  const shareholder: DisqualifiedIndividualRow[] = [];
  for (const i of individuals) {
    if (i.category === 'hci') hci.push(i);
    else if (i.category === 'officer') officer.push(i);
    else if (i.category === 'shareholder') shareholder.push(i);
  }
  return { hci, officer, shareholder };
}

function pickHeadcountStr(form: FormSlice): string {
  const v = form.headcount;
  return String(v ?? '').trim();
}

/** True if another row may be added for this category (headcount required; HCI/officer within caps). */
export function canAppendDisqualifiedIndividual(
  category: DisqualifiedIndividualCategory,
  headcountNumber: number,
  hciCount: number,
  officerCount: number,
  hciLimit: number,
  officerLimit: number
): boolean {
  if (!headcountNumber) return false;
  if (category === 'shareholder') return true;
  if (category === 'hci') return hciCount < hciLimit;
  if (category === 'officer') return officerCount < officerLimit;
  return false;
}

export function useDisqualifiedIndividuals(formData: FormSlice, setFormData: any) {
  const [showModal, setShowModal] = useState(false);
  const [modalCategory, setModalCategory] = useState('hci' as DisqualifiedIndividualCategory);
  const [editingId, setEditingId] = useState(null as string | null);
  const [newInd, setNewInd] = useState({ name: '', email: '', title: '' });
  const [saveBusy, setSaveBusy] = useState(false);
  const saveLockRef = useRef(false);

  const individuals = formData.individuals || [];
  const headcountStr = pickHeadcountStr(formData);
  const headcountNumber = +headcountStr || 0;

  const hciLimit = useMemo(() => hciLimitFromHeadcount(headcountNumber), [headcountNumber]);
  const officerLimit = useMemo(() => officerLimitFromHeadcount(headcountNumber), [headcountNumber]);

  const {
    hci: hcis,
    officer: officers,
    shareholder: shareholders,
  } = useMemo(() => partitionDisqualifiedIndividuals(individuals), [individuals]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingId(null);
    setNewInd({ name: '', email: '', title: '' });
  }, []);

  const openModal = useCallback((cat: DisqualifiedIndividualCategory) => {
    setEditingId(null);
    setModalCategory(cat);
    setNewInd({ name: '', email: '', title: '' });
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((row: DisqualifiedIndividualRow) => {
    setEditingId(row.id);
    setModalCategory(row.category);
    setNewInd({ name: row.name, email: row.email || '', title: row.title || '' });
    setShowModal(true);
  }, []);

  const save = useCallback(() => {
    if (saveLockRef.current) return;
    const name = newInd.name.trim();
    const email = typeof newInd.email === 'string' ? newInd.email.trim() : '';
    const title = typeof newInd.title === 'string' ? newInd.title.trim() : '';
    if (!name) return;

    saveLockRef.current = true;
    setSaveBusy(true);
    const releaseSave = () =>
      queueMicrotask(() => {
        saveLockRef.current = false;
        setSaveBusy(false);
      });
    try {
      if (editingId) {
        setFormData((p: FormSlice) => ({
          ...p,
          individuals: (p.individuals || []).map((i) => (i.id === editingId ? { ...i, name, email, title } : i)),
        }));
        closeModal();
        return;
      }

      if (
        !canAppendDisqualifiedIndividual(
          modalCategory,
          headcountNumber,
          hcis.length,
          officers.length,
          hciLimit,
          officerLimit
        )
      ) {
        return;
      }

      setFormData((p: FormSlice) => ({
        ...p,
        individuals: [...(p.individuals || []), { id: newIndividualId(), name, email, title, category: modalCategory }],
      }));
      closeModal();
    } finally {
      releaseSave();
    }
  }, [
    newInd.name,
    newInd.email,
    newInd.title,
    modalCategory,
    editingId,
    hcis.length,
    officers.length,
    headcountNumber,
    hciLimit,
    officerLimit,
    setFormData,
    closeModal,
  ]);

  const modalCategoryAtCap = useMemo(() => {
    if (editingId) return false;
    if (!headcountNumber) return true;
    return !canAppendDisqualifiedIndividual(
      modalCategory,
      headcountNumber,
      hcis.length,
      officers.length,
      hciLimit,
      officerLimit
    );
  }, [editingId, modalCategory, headcountNumber, hcis.length, officers.length, hciLimit, officerLimit]);

  const remove = useCallback(
    (id: string) => {
      setFormData((p: FormSlice) => ({
        ...p,
        individuals: (p.individuals || []).filter((i) => i.id !== id),
      }));
    },
    [setFormData]
  );

  const onHeadcountChange = useCallback(
    (value: string) => setFormData((p: FormSlice) => ({ ...p, headcount: value })),
    [setFormData]
  );

  return {
    individuals,
    headcountNumber,
    hciLimit,
    officerLimit,
    hcis,
    officers,
    shareholders,
    showModal,
    editingId,
    newInd,
    setNewInd,
    openModal,
    openEditModal,
    closeModal,
    save,
    saveBusy,
    remove,
    onHeadcountChange,
    modalCategoryAtCap,
  };
}
